/* global React, ReactDOM */
(function () {
  'use strict';
  const { useState, useEffect, useCallback } = React;
  const FX = (n) => `€${Number(n).toFixed(2)}`;
  const LOGO_URL = window.KOR_LOGO || 'https://cdn.shopify.com/s/files/1/0990/2833/6982/files/Korallion_W.png?v=1777304349';
  const VIDEO_URL = window.KOR_VIDEO || '';

  const Icon = ({ name, size = 16 }) => {
    const s = { width: size, height: size, fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
    switch (name) {
      case 'cart': return React.createElement('svg', { viewBox: '0 0 24 24', ...s }, React.createElement('path', { d: 'M3 5h2l2.5 11h11l2-8H7' }), React.createElement('circle', { cx: 9, cy: 20, r: 1.4 }), React.createElement('circle', { cx: 17, cy: 20, r: 1.4 }));
      case 'user': return React.createElement('svg', { viewBox: '0 0 24 24', ...s }, React.createElement('circle', { cx: 12, cy: 8, r: 4 }), React.createElement('path', { d: 'M4 21c1-4 4-6 8-6s7 2 8 6' }));
      case 'close': return React.createElement('svg', { viewBox: '0 0 24 24', ...s }, React.createElement('path', { d: 'M5 5l14 14M19 5L5 19' }));
      case 'arrow': return React.createElement('svg', { viewBox: '0 0 24 24', ...s }, React.createElement('path', { d: 'M4 12h16M14 6l6 6-6 6' }));
      case 'coral': return React.createElement('span', { style: { fontSize: size, color: 'var(--kor-green)', lineHeight: 1, fontFamily: 'var(--kor-mono)' } }, '→');
      case 'leaf': return React.createElement('svg', { viewBox: '0 0 24 24', ...s }, React.createElement('path', { d: 'M5 19c0-7 5-13 14-14-1 9-7 14-14 14zM5 19l7-7' }));
      case 'return': return React.createElement('svg', { viewBox: '0 0 24 24', ...s }, React.createElement('path', { d: 'M3 7h13a5 5 0 010 10h-3M3 7l4-4M3 7l4 4' }));
      case 'truck': return React.createElement('svg', { viewBox: '0 0 24 24', ...s }, React.createElement('path', { d: 'M3 7h11v9H3zM14 11h4l3 3v2h-7' }), React.createElement('circle', { cx: 7, cy: 18, r: 1.5 }), React.createElement('circle', { cx: 17, cy: 18, r: 1.5 }));
      case 'shield': return React.createElement('svg', { viewBox: '0 0 24 24', ...s }, React.createElement('path', { d: 'M12 3l8 3v6c0 5-4 8-8 9-4-1-8-4-8-9V6z' }));
      default: return null;
    }
  };

  function useReveal() {
    useEffect(() => {
      const revealVisible = () => {
        document.querySelectorAll('.kor-reveal:not(.is-in)').forEach((el) => {
          const r = el.getBoundingClientRect();
          if (r.top < window.innerHeight - 40 && r.bottom > 0) el.classList.add('is-in');
        });
      };
      requestAnimationFrame(() => requestAnimationFrame(revealVisible));
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
      }, { threshold: 0.12, rootMargin: '0px 0px -60px 0px' });
      document.querySelectorAll('.kor-reveal').forEach((el) => io.observe(el));
      return () => io.disconnect();
    }, []);
  }

  function useCart() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const fetchCart = useCallback(async () => {
      try { const res = await fetch('/cart.js'); const data = await res.json(); setItems(data.items || []); } catch (e) { console.error(e); }
    }, []);
    useEffect(() => { fetchCart(); }, [fetchCart]);
    const add = useCallback(async (variantId, qty = 1) => {
      setLoading(true);
      try { await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: variantId, quantity: qty }) }); await fetchCart(); setOpen(true); } catch (e) { console.error(e); } finally { setLoading(false); }
    }, [fetchCart]);
    const update = useCallback(async (key, qty) => {
      try { await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity: qty }) }); await fetchCart(); } catch (e) { console.error(e); }
    }, [fetchCart]);
    const remove = useCallback(async (key) => { await update(key, 0); }, [update]);
    const subtotal = items.reduce((s, i) => s + i.final_line_price / 100, 0);
    const impact = items.reduce((s, i) => s + (i.final_line_price / 100) * 0.01, 0);
    const count = items.reduce((s, i) => s + i.quantity, 0);
    return { items, open, setOpen, add, update, remove, subtotal, impact, count, loading, fetchCart };
  }

  function Toast({ message, open }) {
    return React.createElement('div', { className: `kor-toast ${open ? 'is-open' : ''}` }, React.createElement('span', { className: 'kor-toast__dot' }), message);
  }

  function Nav({ scrolled, light }) {
    const cart = useCartContext();
    const [isScrolled, setIsScrolled] = useState(scrolled || false);
    const [menuOpen, setMenuOpen] = useState(false);
    useEffect(() => {
      const onScroll = () => setIsScrolled(window.scrollY > 60);
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
      return () => window.removeEventListener('scroll', onScroll);
    }, []);
    useEffect(() => {
      document.body.style.overflow = menuOpen ? 'hidden' : '';
      return () => { document.body.style.overflow = ''; };
    }, [menuOpen]);
    return React.createElement(React.Fragment, null,
      React.createElement('nav', { className: `kor-nav ${isScrolled ? 'is-scrolled' : ''} ${scrolled ? 'is-light' : ''}` },
        React.createElement('a', { href: '/', className: 'kor-nav__logo' }, React.createElement('img', { src: LOGO_URL, alt: 'Korallion — Mode eco-responsable' })),
        React.createElement('div', { className: 'kor-nav__links' },
          React.createElement('a', { href: '/', className: 'kor-nav__link' }, 'Accueil'),
          React.createElement('a', { href: '/collections/all', className: 'kor-nav__link' }, 'Collection'),
          React.createElement('a', { href: '/pages/engagement', className: 'kor-nav__link kor-nav__link--impact' }, 'Notre Impact'),
          React.createElement('a', { href: '/pages/contact', className: 'kor-nav__link' }, 'Contact')
        ),
        React.createElement('div', { className: 'kor-nav__right' },
          React.createElement('a', { href: 'https://shopify.com/99028336982/account', className: 'kor-nav__icon', 'aria-label': 'Mon compte' }, React.createElement(Icon, { name: 'user', size: 18 })),
          React.createElement('button', { className: 'kor-nav__icon', 'aria-label': 'Panier', onClick: () => cart.setOpen(true) },
            React.createElement(Icon, { name: 'cart', size: 18 }),
            cart.count > 0 && React.createElement('span', { className: 'kor-nav__cart-count' }, cart.count)
          ),
          React.createElement('a', { href: '/collections/all', className: 'kor-btn kor-btn--solid-ivory kor-nav__shop' }, 'Shop ', React.createElement('span', { style: { marginLeft: 4 } }, '→')),
          React.createElement('button', {
            className: `kor-nav__burger ${menuOpen ? 'is-open' : ''}`,
            onClick: () => setMenuOpen(!menuOpen),
            'aria-label': 'Menu'
          },
            React.createElement('span', null),
            React.createElement('span', null),
            React.createElement('span', null)
          )
        )
      ),
      React.createElement('div', { className: `kor-nav__mobile ${menuOpen ? 'is-open' : ''}` },
        React.createElement('a', { href: '/', onClick: () => setMenuOpen(false) }, 'Accueil'),
        React.createElement('a', { href: '/collections/all', onClick: () => setMenuOpen(false) }, 'Collection'),
        React.createElement('a', { href: '/pages/engagement', className: 'impact', onClick: () => setMenuOpen(false) }, 'Notre Impact'),
        React.createElement('a', { href: '/pages/contact', onClick: () => setMenuOpen(false) }, 'Contact')
      )
    );
  }

  const CartContext = React.createContext(null);
  const useCartContext = () => React.useContext(CartContext);

  function Hero() {
    const videoRef = React.useRef(null);
    useEffect(() => {
      const v = videoRef.current;
      if (!v) return;
      v.muted = true;
      v.setAttribute('muted', '');
      v.setAttribute('webkit-playsinline', 'true');
      const tryPlay = () => { v.play().catch(() => {}); };
      tryPlay();
      const onClick = () => tryPlay();
      document.addEventListener('touchstart', onClick, { once: true });
      document.addEventListener('click', onClick, { once: true });
      return () => {
        document.removeEventListener('touchstart', onClick);
        document.removeEventListener('click', onClick);
      };
    }, []);
    return React.createElement('section', { className: 'kor-hero' },
      React.createElement('div', { className: 'kor-hero__bg' }, React.createElement('video', { ref: videoRef, autoPlay: true, muted: true, loop: true, playsInline: true, 'webkit-playsinline': 'true' }, React.createElement('source', { src: VIDEO_URL, type: 'video/mp4' }))),
      React.createElement('div', { className: 'kor-hero__overlay' }),
      React.createElement('div', { className: 'kor-hero__grain' }),
      React.createElement('div', { className: 'kor-hero__content' },
        React.createElement('div', { className: 'kor-hero__main' },
          React.createElement('div', { className: 'kor-hero__eyebrow kor-eyebrow kor-green kor-reveal' }, React.createElement('span', { className: 'kor-hero__eyebrow-dot' }), '1% de chaque vente pour Coral Guardian'),
          React.createElement('h1', { className: 'kor-display kor-hero__title kor-reveal', 'data-delay': '1' }, 'La mode qui ', React.createElement('span', { className: 'kor-orange' }, 'prot\u00e8ge'), ' ce qu\'elle c\u00e9l\u00e8bre.'),
          React.createElement('p', { className: 'kor-hero__sub kor-reveal', 'data-delay': '2' }, "Mode durable et \u00e9thique, con\u00e7ue pour ceux qui refusent de laisser l\u2019oc\u00e9an derri\u00e8re eux."),
          React.createElement('div', { className: 'kor-hero__ctas kor-reveal', 'data-delay': '3' },
            React.createElement('a', { href: '/collections/all', className: 'kor-btn kor-btn--solid-ivory' }, 'Découvrir la collection'),
            React.createElement('a', { href: '/pages/engagement', className: 'kor-btn kor-btn--ghost-light' }, 'Notre impact ', React.createElement('span', null, '→'))
          )
        ),
        React.createElement('div', { className: 'kor-hero__meta kor-reveal', 'data-delay': '4' },
          React.createElement('div', { className: 'kor-hero__meta-line' }, 'NAOS — COLLECTION FONDATRICE'),
          React.createElement('div', { className: 'kor-hero__meta-line' }, 'SS26 — DISPONIBLE MAINTENANT'),
          React.createElement('div', { className: 'kor-hero__meta-line' }, 'LIVRAISON FRANCE')
        )
      ),
      React.createElement('div', { className: 'kor-hero__scroll' }, 'SCROLL', React.createElement('span', { className: 'kor-hero__scroll-line' }))
    );
  }

  function ImpactBar() {
    const items = [
      { icon: 'coral', strong: '1% pour Coral Guardian', hint: 'Restauration des récifs coralliens' },
      { icon: 'leaf', strong: 'GOTS · OEKO-TEX · PETA · Fair Wear', hint: 'Certifié de bout en bout' },
      { icon: 'return', strong: 'Retours gratuits 30 jours', hint: 'Problème de taille, pas convaincu ?' },
      { icon: 'truck', strong: 'Livraison offerte des 100 euros', hint: 'France metropolitaine' }
    ];
    return React.createElement('section', { className: 'kor-impact' },
      ...items.map((it, i) => React.createElement('div', { className: 'kor-impact__item', key: i },
        React.createElement('span', { className: 'kor-impact__icon' }, React.createElement(Icon, { name: it.icon, size: 14 })),
        React.createElement('div', null, React.createElement('span', { className: 'kor-impact__strong' }, it.strong), React.createElement('span', { className: 'kor-impact__hint' }, it.hint))
      ))
    );
  }

  function ProductCard({ product }) {
    const cart = useCartContext();
    const img = product.image || '';
    const img2 = product.image2 || '';
    const [showSizes, setShowSizes] = useState(false);
    const [selectedSize, setSelectedSize] = useState(null);
    const [adding, setAdding] = useState(false);
    const sizes = ['XS', 'S', 'M', 'L', 'XL'];
    const handleAdd = async () => {
      if (!selectedSize) return;
      setAdding(true);
      try {
        const res = await fetch('/products/' + product.id + '.js');
        const data = await res.json();
        const variant = data.variants.find(v => v.title === selectedSize && v.available);
        if (variant) {
          await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: variant.id, quantity: 1 }) });
          await cart.fetchCart();
          cart.setOpen(true);
          setShowSizes(false);
          setSelectedSize(null);
        } else {
          window.location.href = '/products/' + product.id;
        }
      } catch(e) { window.location.href = '/products/' + product.id; }
      setAdding(false);
    };
    return React.createElement('article', { className: 'kor-card kor-reveal', style: { position: 'relative' } },
      React.createElement('a', { href: '/products/' + product.id, className: 'kor-card__media' },
        React.createElement('span', { className: 'kor-card__tag' }, product.collection || 'UNISEXE'),
        img ? React.createElement('img', { src: img, alt: product.name, className: 'kor-card__img-main' }) : React.createElement('div', { className: 'kor-ph kor-ph--ocean', style: { position: 'absolute', inset: 0 } }),
        img2 ? React.createElement('img', { src: img2, alt: product.name, className: 'kor-card__img-alt' }) : null,
        React.createElement('div', { className: 'kor-card__quick', onClick: (e) => { e.preventDefault(); window.location.href = '/products/' + product.id; } }, 'Voir le produit ', React.createElement(Icon, { name: 'arrow', size: 14 }))
      ),
      React.createElement('div', { className: 'kor-card__head' }, React.createElement('h3', { className: 'kor-card__name' }, product.name), React.createElement('span', { className: 'kor-card__price' }, FX(product.price))),
      React.createElement('div', { className: 'kor-card__meta' }, product.collection || 'Naos'),
      React.createElement('div', { className: 'kor-card__impact' },
        React.createElement('span', { className: 'kor-card__impact-mark' }, React.createElement(Icon, { name: 'coral', size: 14 })),
        React.createElement('span', null, React.createElement('strong', null, FX(product.impactAmount)), " revers\u00e9s \u00e0 Coral Guardian")
      ),
      !showSizes
        ? React.createElement('button', { className: 'kor-btn kor-btn--solid-black kor-btn--block', onClick: () => setShowSizes(true) }, 'Ajouter au panier →')
        : React.createElement('div', { style: { border: '1px solid var(--kor-line)', padding: '16px', background: 'var(--kor-ivory)' } },
            React.createElement('div', { style: { fontFamily: 'var(--kor-mono)', fontSize: '10px', letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: '10px', display: 'flex', justifyContent: 'space-between' } },
              React.createElement('span', null, 'Choisir la taille'),
              React.createElement('button', { style: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: 'var(--kor-mute)' }, onClick: () => { setShowSizes(false); setSelectedSize(null); } }, 'x')
            ),
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: '6px', marginBottom: '12px' } },
              sizes.map(s => {
                const isOut = product.soldOut && product.soldOut.includes(s);
                return React.createElement('button', {
                  key: s, disabled: isOut,
                  style: { padding: '10px 0', border: '1px solid', fontSize: '11px', fontFamily: 'var(--kor-mono)', cursor: isOut ? 'not-allowed' : 'pointer', borderColor: selectedSize === s ? 'var(--kor-black)' : 'var(--kor-line)', background: selectedSize === s ? 'var(--kor-black)' : 'transparent', color: isOut ? 'rgba(10,10,10,0.25)' : selectedSize === s ? 'var(--kor-ivory)' : 'var(--kor-black)', textDecoration: isOut ? 'line-through' : 'none' },
                  onClick: () => !isOut && setSelectedSize(s)
                }, s);
              })
            ),
            React.createElement('button', { className: 'kor-btn kor-btn--solid-black kor-btn--block', style: { opacity: !selectedSize ? 0.5 : 1, fontSize: '11px', padding: '14px' }, disabled: !selectedSize || adding, onClick: handleAdd }, adding ? 'Ajout en cours...' : 'Confirmer →')
          )
    );
  }

  function ProductsGrid({ products }) {
    return React.createElement('section', { className: 'kor-products', id: 'collection' },
      React.createElement('div', { className: 'kor-products__head kor-reveal' },
        React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '8px' } },
          React.createElement('h2', { className: 'kor-display kor-products__title' }, 'Portez votre ', React.createElement('span', { className: 'kor-products__title-orange' }, 'impact.')),
        ),
        React.createElement('p', { className: 'kor-products__sub' }, "NAOS \u2014 \u00c9dition Fondatrice. Deux pi\u00e8ces en coton bio certifi\u00e9. Con\u00e7ues pour durer, port\u00e9es pour agir. Chaque vente finance la restauration des r\u00e9cifs coralliens.")
      ),
      React.createElement('div', { className: 'kor-products__grid' }, ...products.map((p) => React.createElement(ProductCard, { key: p.id, product: p })))
    );
  }

  function CoralGuardian() {
    return React.createElement('section', { className: 'kor-cg', id: 'impact' },
      React.createElement('div', { className: 'kor-cg__media', style: { backgroundImage: 'url(https://cdn.shopify.com/s/files/1/0990/2833/6982/files/Julien_Holleville_Coral_Guardian._1.jpg?v=1778531372)', backgroundSize: 'contain', backgroundPosition: 'center center', backgroundColor: '#0A0A0A', backgroundRepeat: 'no-repeat' } }),
      React.createElement('div', { className: 'kor-cg__body' },
        React.createElement('div', { className: 'kor-cg__badge kor-reveal' }, 'Partenaire officiel Korállion : Coral Guardian'),
        React.createElement('h2', { className: 'kor-display kor-cg__title kor-reveal', 'data-delay': '1' }, 'Protéger ce qui ', React.createElement('span', { className: 'kor-orange' }, 'nous'), ' inspire.'),
        React.createElement('p', { className: 'kor-cg__text kor-reveal', 'data-delay': '2' }, "L'Océan a construit notre marque. Aujourd'hui, on lui rend la pareille. Via Coral Guardian, chaque vente Korállion finance l'adoption, la transplantation et le suivi à long terme de colonies de coraux sur trois sites actifs en Indonesie, en Espagne et au Kenya."),
        React.createElement('div', { className: 'kor-cg__stats kor-reveal', 'data-delay': '3' },
          React.createElement('div', null, React.createElement('div', { className: 'kor-cg__stat-num' }, '1%'), React.createElement('div', { className: 'kor-cg__stat-label' }, 'de chaque vente', React.createElement('br'), 'reversé directement')),
          React.createElement('div', null, React.createElement('div', { className: 'kor-cg__stat-num' }, '3'), React.createElement('div', { className: 'kor-cg__stat-label' }, 'projets actifs', React.createElement('br'), 'dans le monde')),
          React.createElement('div', null, React.createElement('div', { className: 'kor-cg__stat-num' }, '2012'), React.createElement('div', { className: 'kor-cg__stat-label' }, 'protéction des récifs', React.createElement('br'), 'depuis'))
        ),
        React.createElement('div', { className: 'kor-cg__cta-row kor-reveal', 'data-delay': '4' },
          React.createElement('a', { href: '/pages/engagement', className: 'kor-btn kor-btn--ghost-light' }, 'En savoir plus ', React.createElement('span', null, '→')),
          React.createElement('div', { className: 'kor-cg__partner' }, React.createElement('span', { className: 'kor-cg__partner-mark' }), React.createElement('span', null, 'Coral Guardian', React.createElement('br'), React.createElement('span', { style: { opacity: 0.6 } }, 'Partenaire depuis 03/2026')))
        )
      )
    );
  }

 function JoinReef() {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes('@')) return;
    try {
      await fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          g: 'Vm3WaM',
          email: email,
          $fields: '$source',
          $source: 'Korallion - Join the Reef'
        })
      });
    } catch(e) { console.error(e); }
    setAnimating(true);
    setFadeOut(false);
    setTimeout(() => setShowVideo(true), 1800);
    setTimeout(() => setDone(true), 6000);
    setTimeout(() => { setFadeOut(true); setShowVideo(false); }, 6500);
    setTimeout(() => { setAnimating(false); setFadeOut(false); }, 7800);
  };

  return React.createElement(React.Fragment, null,
    animating && React.createElement('div', {
      style: { position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(10,10,10,0.95)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '32px', animation: fadeOut ? 'korFadeOut 1200ms ease forwards' : 'korRevealIn 1200ms ease forwards' }
    },
      React.createElement('button', {
        style: { position: 'absolute', top: '24px', right: '24px', background: 'none', border: '1px solid rgba(242,239,232,0.3)', color: 'var(--kor-ivory)', fontFamily: 'var(--kor-mono)', fontSize: '10px', letterSpacing: '0.18em', textTransform: 'uppercase', padding: '8px 16px', cursor: 'pointer' },
        onClick: () => { setAnimating(false); setShowVideo(false); }
      }, 'Fermer \u00d7'),
      showVideo && React.createElement('video', {
        autoPlay: true, muted: true, playsInline: true,
        style: { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.7 }
      }, React.createElement('source', { src: 'https://cdn.shopify.com/videos/c/o/v/cc73cf0104774f749e991b47e95a3e04.mp4', type: 'video/mp4' })),
      showVideo && React.createElement('div', { style: { position: 'relative', zIndex: 2, textAlign: 'center' } },
        React.createElement('h2', { className: 'kor-display', style: { fontSize: 'clamp(48px,8vw,120px)', color: 'var(--kor-ivory)', lineHeight: 0.92, opacity: 0, animation: 'korRevealIn 1600ms cubic-bezier(.2,.7,.2,1) 200ms forwards' } },
          'Bienvenue dans', React.createElement('br', null),
          React.createElement('span', { style: { color: 'var(--kor-orange)' } }, 'le r\u00e9cif.')
        ),
        React.createElement('div', { style: { fontFamily: 'var(--kor-mono)', fontSize: '11px', letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--kor-green)', marginTop: '16px', opacity: 0, animation: 'korRevealIn 1400ms cubic-bezier(.2,.7,.2,1) 1400ms forwards' } }, 'CHAQUE PI\u00c8CE QUE TU PORTERAS RESTAURE LES R\u00c9CIFS.')
      )
    ),
    React.createElement('section', { className: 'kor-join' },
      React.createElement('div', { className: 'kor-join__inner' },
        React.createElement('div', { className: 'kor-join__eyebrow kor-eyebrow kor-reveal' }, "Newsletter \u00b7 Nouveaut\u00e9s \u00b7 Rapport"),
        React.createElement('h2', { className: 'kor-display kor-join__title kor-reveal', 'data-delay': '1' }, React.createElement('span', { className: 'kor-orange' }, 'Join '), 'the reef.'),
        React.createElement('p', { className: 'kor-join__sub kor-reveal', 'data-delay': '2' }, "Un email par mois. Nouvelles pi\u00e8ces, rapports d'impact Korallion. Z\u00e9ro bruit."),
        !done
          ? React.createElement('form', { className: 'kor-join__form kor-reveal', 'data-delay': '3', onSubmit: submit },
              React.createElement('input', { className: 'kor-join__input', type: 'email', placeholder: 'votre@email.com', value: email, onChange: (e) => setEmail(e.target.value) }),
              React.createElement('button', { className: 'kor-join__btn', type: 'submit' }, 'Rejoindre \u2192')
            )
          : React.createElement('div', { style: { fontFamily: 'var(--kor-mono)', fontSize: '12px', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--kor-green)', animation: 'korRevealIn 600ms cubic-bezier(.2,.7,.2,1) forwards' } }, "\u2713 Bienvenue dans le r\u00e9cif.")
      )
    )
  );
}

  function Footer() {
    return React.createElement('footer', { className: 'kor-foot' },
      React.createElement('div', { className: 'kor-foot__top' },
        React.createElement('div', { className: 'kor-foot__brand' },
          React.createElement('div', { className: 'kor-foot__logo' }, React.createElement('img', { src: LOGO_URL, alt: 'Korallion' })),
          React.createElement('p', { className: 'kor-foot__tag' }, "Mode éco-responsable pour ceux qui ne peuvent pas vivre loin de l'Océan. Fabriqué équitablement. Porte ton Impact. 1% a Coral Guardian.")
        ),
        React.createElement('div', null,
          React.createElement('div', { className: 'kor-foot__col-title' }, 'Boutique'),
          React.createElement('ul', { className: 'kor-foot__list' },
            React.createElement('li', null, React.createElement('a', { href: '/collections/all' }, 'Collection NAOS')),
            React.createElement('li', null, React.createElement('a', { href: '/products/naos-t-shirt-fondateur' }, 'T-Shirt ')),
            React.createElement('li', null, React.createElement('a', { href: '/products/naos-pull-fondateur' }, 'Pull'))
          )
        ),
        React.createElement('div', null,
          React.createElement('div', { className: 'kor-foot__col-title' }, 'Impact'),
          React.createElement('ul', { className: 'kor-foot__list' },
            React.createElement('li', null, React.createElement('a', { href: '/pages/engagement' }, 'Notre impact')),
            React.createElement('li', null, React.createElement('a', { href: '/pages/engagement' }, 'Coral Guardian')),
            React.createElement('li', null, React.createElement('a', { href: '/pages/engagement' }, 'Nos matieres'))
          )
        ),
        React.createElement('div', null,
          React.createElement('div', { className: 'kor-foot__col-title' }, 'Aide'),
          React.createElement('ul', { className: 'kor-foot__list' },
            React.createElement('li', null, React.createElement('a', { href: '/pages/contact' }, 'Contact')),
            React.createElement('li', null, React.createElement('a', { href: '/policies/shipping-policy' }, 'Livraison')),
            React.createElement('li', null, React.createElement('a', { href: '/policies/refund-policy' }, 'Retours'))
          )
        )
      ),
      React.createElement('div', { className: 'kor-foot__bottom' },
        React.createElement('span', null, '© 2025 Korallion · Montpellier, France'),
        React.createElement('span', null, React.createElement('a', { href: '/policies/privacy-policy' }, 'Confidentialite'), ' · ', React.createElement('a', { href: '/policies/terms-of-service' }, 'CGV'))
      )
    );
  }

  function CartDrawer() {
    const cart = useCartContext();
    const { items, open, setOpen, update, remove, subtotal, impact } = cart;
    return React.createElement(React.Fragment, null,
      React.createElement('div', { className: `kor-cart-overlay ${open ? 'is-open' : ''}`, onClick: () => setOpen(false) }),
      React.createElement('aside', { className: `kor-cart ${open ? 'is-open' : ''}` },
        React.createElement('div', { className: 'kor-cart__head' },
          React.createElement('span', { className: 'kor-cart__title' }, 'Panier (' + items.reduce((s, i) => s + i.quantity, 0) + ')'),
          React.createElement('button', { className: 'kor-cart__close', onClick: () => setOpen(false) }, React.createElement(Icon, { name: 'close', size: 18 }))
        ),
        React.createElement('div', { className: 'kor-cart__body' },
          items.length === 0
            ? React.createElement('div', { className: 'kor-cart__empty' }, React.createElement(Icon, { name: 'cart', size: 40 }), 'Votre panier est vide', React.createElement('a', { href: '/collections/all', className: 'kor-btn kor-btn--solid-black' }, 'Decouvrir la collection'))
            : items.map((item) => React.createElement('div', { className: 'kor-cart-item', key: item.key },
                React.createElement('div', { className: 'kor-cart-item__media' }, item.image ? React.createElement('img', { src: item.image, alt: item.title, style: { width: '100%', height: '100%', objectFit: 'cover' } }) : null),
                React.createElement('div', null,
                  React.createElement('div', { className: 'kor-cart-item__head' }, React.createElement('span', { className: 'kor-cart-item__name' }, item.product_title), React.createElement('span', { className: 'kor-cart-item__price' }, FX(item.final_line_price / 100))),
                  React.createElement('div', { className: 'kor-cart-item__meta' }, item.variant_title),
                  React.createElement('div', { className: 'kor-cart-item__impact' }, '→ ' + FX((item.final_line_price / 100) * 0.01) + ' reversés à Coral Guardian'),
                  React.createElement('div', { className: 'kor-cart-item__row' },
                    React.createElement('div', { className: 'kor-qty' },
                      React.createElement('button', { onClick: () => update(item.key, item.quantity - 1) }, '−'),
                      React.createElement('span', { className: 'kor-qty__val' }, item.quantity),
                      React.createElement('button', { onClick: () => update(item.key, item.quantity + 1) }, '+')
                    ),
                    React.createElement('button', { className: 'kor-cart-item__remove', onClick: () => remove(item.key) }, 'Supprimer')
                  )
                )
              ))
        ),
        items.length > 0 && React.createElement('div', { className: 'kor-cart__foot' },
          React.createElement('div', { className: 'kor-cart__impact-line' }, React.createElement('span', null, 'Votre impact'), React.createElement('strong', null, FX(impact) + ' → Coral Guardian')),
          React.createElement('div', { className: 'kor-cart__totals' }, React.createElement('span', null, 'Sous-total'), React.createElement('span', { className: 'kor-cart__totals-val' }, FX(subtotal))),
          React.createElement('a', { href: '/checkout', className: 'kor-btn kor-btn--solid-black kor-btn--block' }, 'Commander →')
        )
      )
    );
  }

  function Accordion({ title, children, defaultOpen }) {
    const [open, setOpen] = useState(defaultOpen || false);
    return React.createElement('div', { className: 'kor-acc ' + (open ? 'is-open' : '') },
      React.createElement('button', { className: 'kor-acc__btn', onClick: () => setOpen(!open) }, title, React.createElement('span', { className: 'kor-acc__icon' })),
      React.createElement('div', { className: 'kor-acc__panel' },
        React.createElement('div', { className: 'kor-acc__inner' }, children)
      )
    );
  }

  function HomeApp() {
    const cart = useCart();
    const products = window.KOR_PRODUCTS || [];
    const [toast, setToast] = useState({ open: false, msg: '' });
    useReveal();
    useEffect(() => {
      const t = setTimeout(() => { document.querySelectorAll('.kor-reveal:not(.is-in)').forEach((el) => { const r = el.getBoundingClientRect(); if (r.top < window.innerHeight - 60) el.classList.add('is-in'); }); }, 60);
      return () => clearTimeout(t);
    }, []);
    return React.createElement(CartContext.Provider, { value: cart },
      React.createElement(Nav, null),
      React.createElement(Hero),
      React.createElement(ImpactBar),
      React.createElement(ProductsGrid, { products }),
      React.createElement(CoralGuardian),
      React.createElement(JoinReef),
      React.createElement(Footer),
      React.createElement(CartDrawer),
      React.createElement(Toast, { message: toast.msg, open: toast.open })
    );
  }

  function CollectionApp() {
    const cart = useCart();
    const products = window.KOR_PRODUCTS || [];
    useReveal();
    return React.createElement(CartContext.Provider, { value: cart },
      React.createElement(Nav, { scrolled: true, light: true }),
      React.createElement('div', { style: { paddingTop: 'var(--kor-nav-h)', minHeight: '100vh', background: 'var(--kor-ivory)' } },
        React.createElement('div', { style: { padding: '0 var(--kor-pad-x) clamp(60px,10vh,120px)' } },
          React.createElement(ProductsGrid, { products })
        )
      ),
      React.createElement('section', { style: { position: 'relative', background: 'var(--kor-black)', color: 'var(--kor-ivory)', overflow: 'hidden', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
        React.createElement('video', { autoPlay: true, muted: true, loop: true, playsInline: true, style: { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'saturate(0.4) contrast(1.1)', opacity: 0.45 } },
          React.createElement('source', { src: 'https://cdn.shopify.com/videos/c/o/v/50f8bb1fa6ab46f39a9750dc2ff9abe7.mp4', type: 'video/mp4' })
        ),
        React.createElement('div', { style: { position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(10,10,10,0.6) 0%, rgba(10,10,10,0.3) 50%, rgba(10,10,10,0.7) 100%)' } }),
        React.createElement('div', { style: { position: 'relative', zIndex: 2, textAlign: 'center', padding: 'clamp(80px,12vh,140px) var(--kor-pad-x)', maxWidth: '800px', margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0' } },
          React.createElement('div', { style: { fontFamily: 'var(--kor-mono)', fontSize: '11px', letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--kor-orange)', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '16px' } },
            React.createElement('span', { style: { width: '28px', height: '1px', background: 'currentColor', display: 'inline-block' } }),
            'Collection Fondatrice',
            React.createElement('span', { style: { width: '28px', height: '1px', background: 'currentColor', display: 'inline-block' } })
          ),
          React.createElement('h2', { className: 'kor-display', style: { fontSize: 'clamp(100px,16vw,220px)', lineHeight: 0.85, marginBottom: '8px' } }, 'NAOS'),
          React.createElement('div', { style: { fontFamily: 'var(--kor-mono)', fontSize: '11px', letterSpacing: '0.3em', textTransform: 'uppercase', color: 'rgba(242,239,232,0.4)', marginBottom: '270px' } }, 'ναός · grec ancien · temple · fondation'),
          React.createElement('p', { style: { fontSize: 'clamp(15px,1.2vw,18px)', fontWeight: 300, lineHeight: 1.8, color: 'rgba(242,239,232,0.75)', marginBottom: '48px' } }, "Naos désigne le coeur du temple grec. La piece la plus sacrée, la fondation autour de laquelle tout est construit. Chez Korállion, cette première collection porte ce nom pour une raison : elle est la pierre sur laquelle on pose tout le reste."),
          React.createElement('span', { className: 'kor-display', style: { fontSize: 'clamp(32px,4vw,56px)', letterSpacing: '0.08em', textTransform: 'uppercase' } },
            'Soyez les ', React.createElement('span', { style: { color: 'var(--kor-orange)' } }, 'premières pierres'), '.'
          ),
          React.createElement('p', { style: { fontFamily: 'var(--kor-mono)', fontSize: '10px', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(242,239,232,0.4)', marginTop: '16px' } }, 'Comme les coraux qui construisent les récifs "pierre par pierre", chaque achat est un geste fondateur.')
        )
      ),
      React.createElement(Footer),
      React.createElement(CartDrawer)
    );
  }

  function PageApp() {
    const cart = useCart();
    return React.createElement(CartContext.Provider, { value: cart },
      React.createElement(Nav, { scrolled: true, light: true }),
      React.createElement(CartDrawer),
      React.createElement('div', null)
    );
  }

function ProductPageApp() {
    const [lightbox, setLightbox] = useState(false);
    const [zoomed, setZoomed] = useState(false);
    const cart = useCart();
    const product = window.KOR_PRODUCT || {};
    const title = product.title || '';
    const type = product.type || '';
    const price = product.price || 0;
    const impact = product.impact || 0;
    const description = product.description || '';
    const images = product.images || [];
    const variants = product.variants || [];
    const products = window.KOR_PRODUCTS || [];
    const [activeImg, setActiveImg] = useState(0);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [adding, setAdding] = useState(false);
    const [toast, setToast] = useState({ open: false, msg: '' });
    const [showSizeGuide, setShowSizeGuide] = useState(false);
    useReveal();

    const showToast = (msg) => { setToast({ open: true, msg }); setTimeout(() => setToast({ open: false, msg: '' }), 2200); };
    const handleAdd = async () => {
      if (!selectedVariant) return;
      setAdding(true);
      await cart.add(selectedVariant.id, 1);
      showToast('Ajoute — ' + title + ' (' + selectedVariant.title + ')');
      setTimeout(() => setAdding(false), 1200);
    };
    const sizeGuideImg = title.toLowerCase().includes('pull')
      ? 'https://cdn.shopify.com/s/files/1/0990/2833/6982/files/Capture_d_ecran_2026-03-23_a_13.58.15.png?v=1774269567'
      : 'https://cdn.shopify.com/s/files/1/0990/2833/6982/files/Capture_d_ecran_2026-03-23_a_14.05.04.png?v=1774269717';

    return React.createElement(CartContext.Provider, { value: cart },
      React.createElement(Nav, { scrolled: true }),
     lightbox && React.createElement('div', {
  style: { position: 'fixed', inset: 0, background: 'rgba(10,10,10,0.95)', zIndex: 400, overflow: zoomed ? 'auto' : 'hidden', cursor: zoomed ? 'zoom-out' : 'zoom-in' },
  onClick: () => { setLightbox(false); setZoomed(false); }
},
  React.createElement('button', {
    style: { position: 'fixed', top: '20px', right: '20px', zIndex: 10, background: 'none', border: '1px solid rgba(242,239,232,0.3)', color: 'var(--kor-ivory)', fontFamily: 'var(--kor-mono)', fontSize: '10px', letterSpacing: '0.18em', textTransform: 'uppercase', padding: '8px 16px', cursor: 'pointer' },
    onClick: (e) => { e.stopPropagation(); setLightbox(false); setZoomed(false); }
  }, 'Fermer \u00d7'),
  React.createElement('div', {
    style: { minWidth: '100%', minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: zoomed ? '40px' : '20px' },
    onClick: (e) => { e.stopPropagation(); setZoomed(!zoomed); }
  },
    React.createElement('img', {
      src: images[activeImg] ? images[activeImg].url : '',
      alt: title,
      style: { width: zoomed ? '160%' : 'auto', maxWidth: zoomed ? 'none' : '90vw', maxHeight: zoomed ? 'none' : '90vh', objectFit: 'contain', transition: 'all 400ms ease', display: 'block' }
    })
  )
),
      showSizeGuide && React.createElement('div', {
        style: { position: 'fixed', inset: 0, background: 'rgba(10,10,10,0.9)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', backdropFilter: 'blur(8px)' },
        onClick: () => setShowSizeGuide(false)
      },
        React.createElement('div', { style: { position: 'relative', maxWidth: '900px', width: '100%', background: 'var(--kor-ivory)', boxShadow: '0 40px 120px rgba(0,0,0,0.4)' }, onClick: (e) => e.stopPropagation() },
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid var(--kor-line)' } },
            React.createElement('span', { style: { fontFamily: 'var(--kor-mono)', fontSize: '11px', letterSpacing: '0.22em', textTransform: 'uppercase' } }, 'Guide des tailles'),
            React.createElement('button', { style: { background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--kor-mono)', fontSize: '11px', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--kor-mute)', display: 'flex', alignItems: 'center', gap: '8px' }, onClick: () => setShowSizeGuide(false) }, 'Fermer x')
          ),
          React.createElement('div', { style: { padding: '16px 24px', borderBottom: '1px solid var(--kor-line)', background: 'rgba(10,10,10,0.03)' } },
            title.toLowerCase().includes('pull') ? null : React.createElement('p', { style: { fontFamily: 'var(--kor-mono)', fontSize: '9px', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--kor-mute)', margin: 0 } }, 'Silhouette oversized pens\u00e9e pour le confort au quotidien. ', React.createElement('span', { style: { color: 'var(--kor-black)', fontWeight: 600 } }, 'Entre deux tailles, optez pour la plus petite.'))
          ),
          React.createElement('div', { style: { padding: '24px' } },
            React.createElement('img', { src: sizeGuideImg, alt: 'Guide des tailles', style: { width: '100%', display: 'block' } })
          )
        )
      ),
      React.createElement('div', { className: 'kor-pdp' },
        React.createElement('div', { className: 'kor-pdp__crumb' }, React.createElement('a', { href: '/' }, 'Accueil'), ' / ', React.createElement('a', { href: '/collections/all' }, 'Collection'), ' / ', React.createElement('span', { style: { color: 'var(--kor-black)' } }, title)),
        React.createElement('div', { className: 'kor-pdp__main' },
          React.createElement('div', { className: 'kor-pdp__gallery' },
            React.createElement('div', { className: 'kor-pdp__gallery-main', style: { cursor: 'zoom-in' }, onClick: () => images.length > 0 && setLightbox(true) },
              images.length > 0
                ? React.createElement(React.Fragment, null,
                    images.map((img, i) => React.createElement('img', { key: i, src: img.url, alt: img.alt || title, style: { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: i === activeImg ? 1 : 0, transition: 'opacity 360ms ease' } })),
                    React.createElement('button', { className: 'kor-pdp__nav kor-pdp__nav--prev', onClick: (e) => { e.stopPropagation(); setActiveImg(activeImg > 0 ? activeImg - 1 : images.length - 1); }, 'aria-label': 'Photo precedente' }, '←'),
                    React.createElement('button', { className: 'kor-pdp__nav kor-pdp__nav--next', onClick: (e) => { e.stopPropagation(); setActiveImg(activeImg < images.length - 1 ? activeImg + 1 : 0); }, 'aria-label': 'Photo suivante' }, '→'),
                    React.createElement('div', { className: 'kor-pdp__dots' }, images.map((_, i) => React.createElement('span', { key: i, className: 'kor-pdp__dot ' + (i === activeImg ? 'is-active' : ''), onClick: (e) => { e.stopPropagation(); setActiveImg(i); } })))
                  )
                : React.createElement('div', { className: 'kor-ph kor-ph--ocean', style: { position: 'absolute', inset: 0 } })
            ),
            images.length > 1 && React.createElement('div', { className: 'kor-pdp__gallery-thumbs' },
              images.map((img, i) => React.createElement('button', { key: i, className: 'kor-pdp__thumb ' + (i === activeImg ? 'is-active' : ''), onClick: () => setActiveImg(i) },
                React.createElement('img', { src: img.url, alt: img.alt || title, style: { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' } })
              ))
            )
          ),
          React.createElement('div', { className: 'kor-pdp__info' },
            React.createElement('div', { className: 'kor-pdp__collection' },
              React.createElement('span', { style: { background: 'var(--kor-orange)', color: 'var(--kor-ivory)', padding: '4px 10px', fontFamily: 'var(--kor-mono)', fontSize: '11px', letterSpacing: '0.22em', textTransform: 'uppercase' } }, 'Produit unisexe')
            ),
            React.createElement('h1', { className: 'kor-pdp__name' }, title),
            React.createElement('div', { className: 'kor-pdp__price-row' }, React.createElement('span', { className: 'kor-pdp__price' }, FX(price)), React.createElement('span', { className: 'kor-pdp__tax' })),
            React.createElement('div', { className: 'kor-pdp__impact' },
              React.createElement('span', { className: 'kor-pdp__impact-mark' }, React.createElement(Icon, { name: 'coral', size: 12 })),
              React.createElement('div', { className: 'kor-pdp__impact-text' }, React.createElement('strong', null, FX(impact)), ' de cet achat sont revers\u00e9s \u00e0 Coral Guardian', React.createElement('small', null, '1% \u2014 financement direct de la restauration des r\u00e9cifs'))
            ),
            React.createElement('div', null,
  !title.toLowerCase().includes('pull') && React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: 'rgba(232,66,10,0.06)', borderLeft: '2px solid var(--kor-orange)', marginBottom: '12px' } },
    React.createElement('span', { style: { fontFamily: 'var(--kor-mono)', fontSize: '9px', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--kor-black)' } }, 'Coupe oversized \u2014 '),
    React.createElement('span', { style: { fontFamily: 'var(--kor-mono)', fontSize: '9px', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--kor-orange)', fontWeight: 600 } }, 'nous conseillons une taille en dessous')
  ),
  React.createElement('div', { className: 'kor-pdp__sizes-head' },
                React.createElement('span', null, 'Taille'),
                React.createElement('a', { href: '#', className: 'kor-pdp__guide', onClick: (e) => { e.preventDefault(); setShowSizeGuide(true); } }, 'Guide des tailles')
              ),
              React.createElement('div', { className: 'kor-pdp__sizes' },
                variants.map((v) => React.createElement('button', { key: v.id, className: 'kor-pdp__size ' + (selectedVariant && selectedVariant.id === v.id ? 'is-active' : ''), disabled: !v.available, onClick: () => setSelectedVariant(v) }, v.title))
              )
            ),
            React.createElement('button', { className: 'kor-btn kor-btn--solid-black kor-btn--block kor-pdp__add', onClick: handleAdd, disabled: !selectedVariant || adding, style: { opacity: !selectedVariant ? 0.5 : 1 } }, adding ? 'Ajout\u00e9' : (selectedVariant ? 'Ajouter au panier' : 'S\u00e9lectionner une taille')),
            React.createElement('div', { className: 'kor-pdp__reassure' },
              React.createElement('div', { className: 'kor-pdp__reassure-item' }, React.createElement(Icon, { name: 'truck', size: 18 }), 'Livraison', React.createElement('br'), 'offerte d\u00e8s 100\u20ac'),
              React.createElement('div', { className: 'kor-pdp__reassure-item' }, React.createElement(Icon, { name: 'return', size: 18 }), 'Retours', React.createElement('br'), 'gratuits 30j'),
              React.createElement('div', { className: 'kor-pdp__reassure-item' }, React.createElement(Icon, { name: 'shield', size: 18 }), 'Con\u00e7u', React.createElement('br'), 'pour durer')
            ),
            React.createElement('div', { className: 'kor-pdp__labels' }, ['GOTS', 'OEKO-TEX', 'PETA approved', 'Fair Wear'].map((l) => React.createElement('span', { key: l, className: 'kor-pdp__label' }, l))),
            React.createElement('div', { className: 'kor-pdp__accordions' },
              [
                { key: 'materials', title: 'Description Produits', content: description },
                { key: 'care', title: 'Labels & certifications', content: React.createElement('div', null,
                  React.createElement('p', { style: { marginBottom: '12px' } }, "Nos pi\u00e8ces sont confectionn\u00e9es \u00e0 partir de textiles premium fournis par Stanley/Stella, reconnu pour ses standards environnementaux et sociaux."),
                  React.createElement('ul', { style: { listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '8px', margin: 0, padding: 0 } },
                    React.createElement('li', { style: { display: 'flex', gap: '10px', alignItems: 'flex-start' } }, React.createElement('span', { style: { color: 'var(--kor-green)', flexShrink: 0 } }, '\u2192'), 'Coton biologique certifi\u00e9 GOTS et OEKO-TEX\u00ae Standard 100.'),
                    React.createElement('li', { style: { display: 'flex', gap: '10px', alignItems: 'flex-start' } }, React.createElement('span', { style: { color: 'var(--kor-green)', flexShrink: 0 } }, '\u2192'), 'PETA-Approved Vegan, sans mati\u00e8res d\'origine animale.'),
                    React.createElement('li', { style: { display: 'flex', gap: '10px', alignItems: 'flex-start' } }, React.createElement('span', { style: { color: 'var(--kor-green)', flexShrink: 0 } }, '\u2192'), 'Membre et Leader de la Fair Wear Foundation, qui \u0153uvre pour des conditions de travail \u00e9quitables.'),
                    React.createElement('li', { style: { display: 'flex', gap: '10px', alignItems: 'flex-start' } }, React.createElement('span', { style: { color: 'var(--kor-green)', flexShrink: 0 } }, '\u2192'), 'Fabriqu\u00e9es fi\u00e8rement au Bangladesh, dans des ateliers audit\u00e9s et responsables.')
                  )
                )},
                { key: 'impact', title: 'Impact Coral Guardian', content: React.createElement('div', null,
                  React.createElement('p', { style: { marginBottom: '12px' } }, FX(impact) + " de cet achat financent la restauration des r\u00e9cifs avec Coral Guardian."),
                  React.createElement('ul', { style: { listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '8px', margin: 0, padding: 0 } },
                    React.createElement('li', { style: { display: 'flex', gap: '10px', alignItems: 'flex-start' } }, React.createElement('span', { style: { color: 'var(--kor-green)', flexShrink: 0 } }, '\u2192'), 'Bouturage de corails.'),
                    React.createElement('li', { style: { display: 'flex', gap: '10px', alignItems: 'flex-start' } }, React.createElement('span', { style: { color: 'var(--kor-green)', flexShrink: 0 } }, '\u2192'), 'Transplantation et suivi scientifique.'),
                    React.createElement('li', { style: { display: 'flex', gap: '10px', alignItems: 'flex-start' } }, React.createElement('span', { style: { color: 'var(--kor-green)', flexShrink: 0 } }, '\u2192'), '3 sites actifs en Indon\u00e9sie, Espagne et Kenya.')
                  )
                )}
              ].map((acc) => React.createElement(Accordion, { key: acc.key, title: acc.title, defaultOpen: acc.key === 'materials' }, acc.content))
            )
          )
        )
      ),
      products.length > 0 && React.createElement('section', { style: { background: 'var(--kor-ivory)', padding: 'clamp(60px,8vh,100px) var(--kor-pad-x)', borderTop: '1px solid var(--kor-line)' } },
        React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '40px', paddingBottom: '20px', borderBottom: '1px solid var(--kor-line)' } },
          React.createElement('h2', { className: 'kor-display', style: { fontSize: 'clamp(32px,4vw,56px)' } }, 'Vous aimerez aussi'),
          React.createElement('a', { href: '/collections/all', style: { fontFamily: 'var(--kor-mono)', fontSize: '11px', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--kor-mute)' } }, 'Voir tout \u2192')
        ),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 'clamp(24px,4vw,56px)' } },
          products.slice(0, 2).map(p => React.createElement(ProductCard, { key: p.id, product: p }))
        )
      ),
      React.createElement(Footer),
      React.createElement(CartDrawer),
      React.createElement(Toast, { message: toast.msg, open: toast.open })
    );
  }

  const navEl = document.getElementById('kor-nav-only');
  if (navEl) {
    const navCart = useCart();
    ReactDOM.createRoot(navEl).render(
      React.createElement(CartContext.Provider, { value: navCart },
        React.createElement(Nav, { scrolled: true, light: true }),
        React.createElement(CartDrawer)
      )
    );
  }
  const appEl = document.getElementById('kor-app');
  if (appEl) {
    const page = appEl.dataset.page;
    let App;
    if (page === 'product') App = ProductPageApp;
    else if (page === 'collection') App = CollectionApp;
    else if (page === 'page') App = PageApp;
    else App = HomeApp;
    ReactDOM.createRoot(appEl).render(React.createElement(App));
  }
})();